/**
 * Markdown text utilities for copy and insert operations.
 *
 * Uses ReactMarkdown to convert markdown to HTML for clipboard and Word insertion.
 * Ensures consistency with the UI rendering by using the same markdown engine.
 */

import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { renderToString } from "react-dom/server";
import { RE_SUB_SUP } from "./formatter/markdownParser";
import { remarkSubSup } from "./remarkSubSup";

/**
 * Convert markdown to HTML using ReactMarkdown (same as UI rendering).
 * Ensures the copied content matches what the user sees.
 *
 * @param markdown - The markdown content to convert
 * @returns HTML representation with preserved formatting and inline styles for Word
 */
export function markdownToHtml(markdown: string): string {
  try {
    // Render markdown to React component, then to HTML string
    // This ensures consistency with the UI rendering in MessageBubble
    // singleTilde:false — GFM's default single-tilde strikethrough would
    // consume `~2~` before remarkSubSup can turn it into <sub>.
    const element = React.createElement(ReactMarkdown, {
      remarkPlugins: [[remarkGfm, { singleTilde: false }], remarkSubSup],
      children: markdown,
    });
    let html = renderToString(element);

    // Add inline styles for Word compatibility (Word doesn't understand CSS classes)
    // This ensures table borders and other formatting appear correctly when inserted into Word
    html = addInlineStylesToHtml(html);

    return html;
  } catch (err) {
    console.warn("[markdownToHtml] Failed to render markdown to HTML, falling back to plain text:", err);
    // Fallback to plain text if rendering fails
    return `<p>${escapeHtml(markdown)}</p>`;
  }
}

/**
 * Add inline styles to HTML elements for Word compatibility.
 * Word's insertHtml() doesn't understand CSS classes, so we inject inline styles.
 */
function addInlineStylesToHtml(html: string): string {
  let styled = html;

  // Add table borders and styling
  styled = styled.replace(/<table([^>]*)>/g, '<table$1 style="border-collapse: collapse; width: 100%; margin: 6px 0;">');
  styled = styled.replace(/<th([^>]*)>/g, '<th$1 style="border: 1px solid #d1d5db; padding: 8px; text-align: left; font-weight: 700; background-color: #f3f4f6;">');
  styled = styled.replace(/<td([^>]*)>/g, '<td$1 style="border: 1px solid #d1d5db; padding: 8px;">');

  // Add heading styles
  styled = styled.replace(/<h1([^>]*)>/g, '<h1$1 style="font-size: 24px; font-weight: 700; margin: 16px 0 8px 0;">');
  styled = styled.replace(/<h2([^>]*)>/g, '<h2$1 style="font-size: 20px; font-weight: 700; margin: 14px 0 8px 0;">');
  styled = styled.replace(/<h3([^>]*)>/g, '<h3$1 style="font-size: 18px; font-weight: 700; margin: 12px 0 8px 0;">');

  // Add list styles
  styled = styled.replace(/<ul([^>]*)>/g, '<ul$1 style="margin: 6px 0; padding-left: 20px;">');
  styled = styled.replace(/<ol([^>]*)>/g, '<ol$1 style="margin: 6px 0; padding-left: 20px;">');
  styled = styled.replace(/<li([^>]*)>/g, '<li$1 style="margin: 2px 0;">');

  // Add paragraph styles
  styled = styled.replace(/<p([^>]*)>/g, '<p$1 style="margin: 6px 0;">');

  // Add blockquote styles
  styled = styled.replace(/<blockquote([^>]*)>/g, '<blockquote$1 style="border-left: 3px solid #d1d5db; padding-left: 8px; margin: 6px 0; color: #6b7280;">');

  // Add code styles
  styled = styled.replace(/<code([^>]*)>/g, '<code$1 style="font-family: monospace; font-size: 12px; background-color: #f3f4f6; padding: 1px 4px; border-radius: 3px;">');

  // Add pre (code block) styles
  styled = styled.replace(/<pre([^>]*)>/g, '<pre$1 style="font-family: monospace; font-size: 12px; background-color: #f3f4f6; padding: 8px 10px; border-radius: 3px; overflow-x: auto;">');

  // Add strong styles
  styled = styled.replace(/<strong([^>]*)>/g, '<strong$1 style="font-weight: 700;">');

  // Add emphasis styles
  styled = styled.replace(/<em([^>]*)>/g, '<em$1 style="font-style: italic;">');

  // Sub/superscript (from ~sub~ / ^sup^ markers) — Word's insertHtml maps
  // <sub>/<sup> to true vertAlign runs; the inline style is belt-and-braces.
  styled = styled.replace(/<sub([^>]*)>/g, '<sub$1 style="vertical-align: sub; font-size: smaller;">');
  styled = styled.replace(/<sup([^>]*)>/g, '<sup$1 style="vertical-align: super; font-size: smaller;">');

  return styled;
}

/** Escape HTML special characters. */
function escapeHtml(text: string): string {
  const map: { [key: string]: string } = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  };
  return text.replace(/[&<>"']/g, (m) => map[m]);
}

/**
 * Convert markdown to plain text for clipboard and Word insertion.
 * Preserves formatting hints and structure (tables, lists).
 *
 * @param markdown - The markdown content to convert
 * @returns Plain text representation with preserved structure
 */
export function markdownToPlainText(markdown: string): string {
  let text = markdown;

  // Preserve bold formatting with emphasis markers
  text = text.replace(/\*\*([^\*]+)\*\*/g, "$1");
  text = text.replace(/__([^_]+)__/g, "$1");

  // Preserve italic formatting
  text = text.replace(/\*([^\*]+)\*/g, "$1");
  text = text.replace(/_([^_]+)_/g, "$1");

  // Unwrap ~sub~ / ^sup^ markers — plain text can't represent vertAlign
  text = text.replace(RE_SUB_SUP, (_m, sub, sup) => sub ?? sup);

  // Convert links: keep link text, drop URL
  text = text.replace(/\[(.*?)\]\((.*?)\)/g, "$1");

  // Remove heading markers but preserve heading text and add spacing
  text = text.replace(/^### (.*?)$/gm, "$1");
  text = text.replace(/^## (.*?)$/gm, "$1");
  text = text.replace(/^# (.*?)$/gm, "$1");

  // Preserve blockquotes
  text = text.replace(/^> (.*?)$/gm, "> $1");

  // Clean up code blocks while preserving code content
  text = text.replace(/```([\s\S]*?)```/g, (match, code) => {
    const cleanCode = code.trim();
    return `\n${cleanCode}\n`;
  });

  // Remove inline code markers but keep content
  text = text.replace(/`([^`]+)`/g, "$1");

  // Preserve unordered lists
  text = text.replace(/^[-*] /gm, "• ");

  // Preserve ordered lists
  text = text.replace(/^\d+\. /gm, "• ");

  // Clean up multiple consecutive newlines
  text = text.replace(/\n{3,}/g, "\n\n");

  return text.trim();
}

/**
 * Extract plain text from a markdown string, removing all formatting.
 * Used as a fallback for clipboard operations.
 */
export function extractPlainTextContent(markdown: string): string {
  return markdownToPlainText(markdown);
}

/**
 * Copy content to clipboard with both text and HTML formats.
 * Allows formatted content to be pasted into applications like Word.
 *
 * @param text - Plain text version of the content
 * @param html - HTML version for formatted pasting (optional)
 */
export async function copyToClipboard(text: string, html?: string): Promise<void> {
  // If HTML is provided, try to copy both text and HTML formats
  if (html && navigator.clipboard && window.isSecureContext) {
    try {
      const blob = new Blob(
        [
          // HTML format that Word understands
          `<html><body>${html}</body></html>`,
        ],
        { type: "text/html" }
      );
      const data = [new ClipboardItem({ "text/html": blob, "text/plain": new Blob([text], { type: "text/plain" }) })];
      await navigator.clipboard.write(data);
      return;
    } catch (err) {
      // Fallback to text-only if HTML copy fails
      console.warn("[markdownToHtml] HTML clipboard copy failed, falling back to text:", err);
    }
  }

  // Fallback: copy as plain text only
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      // Final fallback for non-secure contexts
      copyViaExecCommand(text);
    }
  } else {
    copyViaExecCommand(text);
  }
}

/** Fallback copy using execCommand for non-secure contexts. */
function copyViaExecCommand(text: string): void {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
}
